import React, { createRef, type FC, type HTMLInputTypeAttribute } from 'react'
import { Box, inputBaseClasses, type SxProps, textFieldClasses, type Theme } from '@mui/material'

import type { ReadonlyRecord } from '../../../../utils'

import { extractDataTestId, wasKeyHit } from '../..'
import { TextField, type TextFieldProps } from './TextField'

export type LengthLimitedFieldProps = Pick<TextFieldProps, 'disabled' | 'autoFocus' | 'slotProps'> &
    Readonly<{ type: Extract<HTMLInputTypeAttribute, 'tel'>, value: readonly string[], onChange: (value: readonly string[]) => void }>

const valueMappers: ReadonlyRecord<LengthLimitedFieldProps['type'], (raw: string) => string | null> = {
    /**
     * Validate if a given input is a single digit (0-9), If it's single digit, it returns the digit as a string else, it returns null
     */
    tel: (rawValue) => (/^\d{1}$/.test(rawValue) ? rawValue : null),
}

const fixedLengthTextFieldWrapperStyle: SxProps<Theme> = { display: 'flex', justifyContent: 'space-between' } as const

const baseNumberedTextFieldProps = {
    required: true,
    fullWidth: false,
    sx: { mr: 1, '&:last-of-type': { mr: 0 } },
    slotProps: {
        input: {
            sx: {
                aspectRatio: '1',
                [`& input.${inputBaseClasses.input}`]: { textAlign: 'center' },
                [[`& input.${inputBaseClasses.input}::-webkit-outer-spin-button', '& input.${inputBaseClasses.input}::-webkit-inner-spin-button`].join(', ')]: {
                    WebkitAppearance: 'none',
                },
            },
        },
    },
} as const

export const LengthLimitedField: FC<LengthLimitedFieldProps> = ({ autoFocus, type, value, onChange, slotProps, disabled, ...props }) => {
    const extractValue = valueMappers[type]
    const parentRef = createRef<HTMLDivElement>()

    return (
        <Box {...props} ref={parentRef} sx={fixedLengthTextFieldWrapperStyle}>
            {value.map((part, k) => (
                <TextField
                    key={k}
                    data-testid={extractDataTestId(props, `-${k + 1}`)}
                    {...baseNumberedTextFieldProps}
                    slotProps={{
                        ...slotProps,
                        input: {
                            ...baseNumberedTextFieldProps.slotProps.input,
                            ...slotProps?.input,
                            sx: {
                                ...baseNumberedTextFieldProps.slotProps.input.sx,
                                ...slotProps?.input?.sx,
                            },
                        },
                    }}
                    sx={{ ...baseNumberedTextFieldProps.sx, width: `calc(100% / ${value.length})` }}
                    type={type}
                    autoFocus={autoFocus && k === 0}
                    value={part}
                    onKeyDown={(e) => {
                        let newPart = part
                        let clearPrevious = false

                        /** Highest the value, more to the right/down the node tree */
                        let move: -1 | 0 | 1 = 0

                        const validatedValue = extractValue(e.key)

                        if (validatedValue !== null) {
                            newPart = validatedValue
                            /** Done on this block, move to the next */
                            move = 1
                        }

                        if (wasKeyHit(e, 'Backspace')) {
                            /** User wants to delete the value */
                            newPart = ''
                            if (!part) {
                                /** Was already empty, move to the previous */
                                move = -1
                                clearPrevious = k > 0
                            }
                        }

                        if (wasKeyHit(e, 'ArrowLeft')) {
                            /** Just move to the previous one */
                            move = -1
                        }

                        if (wasKeyHit(e, 'ArrowRight')) {
                            /** Just move to the next one */
                            move = 1
                        }

                        if (wasKeyHit(e, 'Enter') && k + 1 !== value.length) {
                            /** User wants to move to next value unless they are on the last input */
                            move = 1
                        }

                        const hasChanged = newPart !== part
                        if (hasChanged || clearPrevious) {
                            const update = [...value]
                            if (hasChanged) {
                                update[k] = newPart
                            }
                            if (clearPrevious) {
                                update[k - 1] = ''
                            }
                            onChange(update)
                        }

                        const inputToMoveTo =
                            move !== 0 &&
                            parentRef.current &&
                            Array.from(
                                parentRef.current.querySelectorAll<HTMLInputElement>(
                                    `.${textFieldClasses.root} .${inputBaseClasses.root} input.${inputBaseClasses.input}`
                                )
                            ).find((_, k, all) => all[k - move] === e.target)

                        if (inputToMoveTo) {
                            inputToMoveTo.focus()
                        }
                    }}
                    onPaste={(e) => {
                        const copy = [...value]
                        const parsedValues = e.clipboardData.getData('Text').split('')

                        parsedValues.slice(0, value.length - k).forEach((rawValue, i) => {
                            const validatedValue = extractValue(rawValue)
                            if (validatedValue !== null) {
                                copy[k + i] = validatedValue
                            }
                        })

                        onChange(copy)
                    }}
                    disabled={disabled}
                />
            ))}
        </Box>
    )
}
